import React, {useEffect, useRef} from 'react'
import ApexCharts from 'apexcharts'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

const buildChartOptions = (analysis, intl) => {
  const {
    projectStartDate,
    projectEndDate,
    totalWork,
    plannedCompletedWork,
    actualCompletedWork,
    scheduleStatus,
  } = analysis

  const startTs = new Date(projectStartDate).setHours(0, 0, 0, 0)
  const endTs = new Date(projectEndDate).setHours(0, 0, 0, 0)
  const todayTs = new Date().setHours(0, 0, 0, 0)

  // Gerçekleşen: green if on-track or ahead, red if behind
  // scheduleStatus: 0=OnTrack, 1=Behind, 2=Ahead
  const gerceklesenColor = scheduleStatus === 1 ? '#f1416c' : '#50cd89'

  return {
    series: [
      {
        // Planlanan — dashed blue: linear from (start, 0) → (end, totalWork)
        name: intl.formatMessage({id: 'WORKSTATUS.CHART.FULLPLAN'}),
        type: 'line',
        data: [
          {x: startTs, y: 0},
          {x: endTs, y: parseFloat(totalWork.toFixed(2))},
        ],
      },
      {
        // Yapılması Gereken — solid blue: (start, 0) → (today, plannedCompletedWork)
        name: intl.formatMessage({id: 'WORKSTATUS.CHART.PLANNEDTODATE'}),
        type: 'line',
        data: [
          {x: startTs, y: 0},
          {x: todayTs, y: parseFloat(plannedCompletedWork.toFixed(2))},
        ],
      },
      {
        // Gerçekleşen — single point at today
        name: intl.formatMessage({id: 'WORKSTATUS.CHART.ACTUAL'}),
        type: 'scatter',
        data: [{x: todayTs, y: parseFloat(actualCompletedWork.toFixed(2))}],
      },
    ],
    chart: {
      height: 380,
      type: 'line',
      toolbar: {show: false},
      zoom: {enabled: false},
    },
    stroke: {
      curve: 'straight',
      // Planlanan: dashed, Yapılması Gereken: solid, Gerçekleşen: no stroke
      width: [2, 2, 0],
      dashArray: [6, 0, 0],
    },
    colors: ['#009ef7', '#7239ea', gerceklesenColor],
    markers: {
      size: [0, 0, 10],
      strokeWidth: [0, 0, 2],
      strokeColors: ['transparent', 'transparent', '#ffffff'],
      hover: {sizeOffset: 2},
    },
    dataLabels: {enabled: false},
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: "MMM 'yy",
          day: 'dd MMM',
        },
      },
    },
    yaxis: {
      title: {text: intl.formatMessage({id: 'WORKSTATUS.CHART.YAXIS'})},
      min: 0,
      max: parseFloat((totalWork * 1.1).toFixed(2)),
    },
    legend: {
      position: 'top',
      markers: {
        // Make Planlanan legend show dashed style visually
        width: 20,
        height: 3,
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      x: {format: 'dd MMM yyyy'},
    },
    grid: {strokeDashArray: 4},
  }
}

const WorkStatusChart = ({analysis}) => {
  const intl = useIntl()
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !analysis) return
    if (!analysis.projectStartDate || !analysis.projectEndDate) return

    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    chartInstance.current = new ApexCharts(chartRef.current, buildChartOptions(analysis, intl))
    chartInstance.current.render()

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
        chartInstance.current = null
      }
    }
  }, [analysis, intl])

  if (!analysis.projectStartDate || !analysis.projectEndDate) {
    return null
  }

  return (
    <div className='card mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            {intl.formatMessage({id: 'WORKSTATUS.CHART.TITLE'})}
          </span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {intl.formatMessage({id: 'WORKSTATUS.CHART.SUBTITLE'})}
          </span>
        </h3>
      </div>
      <div className='card-body'>
        <div ref={chartRef} />
      </div>
    </div>
  )
}

WorkStatusChart.propTypes = {
  analysis: PropTypes.shape({
    projectStartDate: PropTypes.string,
    projectEndDate: PropTypes.string,
    totalWork: PropTypes.number,
    plannedCompletedWork: PropTypes.number,
    actualCompletedWork: PropTypes.number,
    scheduleStatus: PropTypes.number,
  }).isRequired,
}

export {WorkStatusChart}
